import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  ArcElement,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { DailyOrderData, OrderCategoryData, RevenueData } from '../types';
import { barChartConfig, lineChartConfig, doughnutChartConfig, chartColors } from '../mockData';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  ArcElement
);

interface DailyOrderChartProps {
  data: DailyOrderData[];
  height?: number;
}

export const DailyOrderChart: React.FC<DailyOrderChartProps> = ({ data, height = 300 }) => {
  const chartData = {
    labels: data.map(item => item.day),
    datasets: [
      {
        label: 'Orders',
        data: data.map(item => item.orders),
        backgroundColor: chartColors.primary,
        borderColor: chartColors.primary,
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  };

  const options = {
    ...barChartConfig,
    plugins: {
      ...barChartConfig.plugins,
      title: {
        display: false,
      },
    },
    scales: {
      ...barChartConfig.scales,
      y: {
        ...barChartConfig.scales?.y,
        ticks: {
          callback: function(value: any) {
            return value.toLocaleString();
          }
        }
      }
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg border" style={{ height }}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">Daily Order Volume</h3>
        <p className="text-sm text-gray-600">Orders and revenue over the last 7 days</p>
      </div>
      <div style={{ height: height - 80 }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

interface OrderCategoryChartProps {
  data: OrderCategoryData[];
  height?: number;
}

export const OrderCategoryChart: React.FC<OrderCategoryChartProps> = ({ data, height = 300 }) => {
  const chartData = {
    labels: data.map(item => item.category),
    datasets: [
      {
        data: data.map(item => item.percentage),
        backgroundColor: data.map(item => item.color),
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverBorderWidth: 3,
      },
    ],
  };

  const options = {
    ...doughnutChartConfig,
    plugins: {
      ...doughnutChartConfig.plugins,
      tooltip: {
        ...doughnutChartConfig.plugins.tooltip,
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed;
            const dataPoint = data[context.dataIndex];
            return `${label}: ${value}% (${dataPoint.count.toLocaleString()} orders)`;
          }
        }
      },
      legend: {
        ...doughnutChartConfig.plugins.legend,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
          },
          generateLabels: function(chart: any) {
            const labels = chart.data.labels;
            
            return labels.map((label: string, i: number) => {
              const dataPoint = data[i];
              return {
                text: `${label} ${dataPoint.percentage}%`,
                fillStyle: dataPoint.color,
                strokeStyle: dataPoint.color,
                lineWidth: 0,
                pointStyle: 'circle',
                hidden: false,
                index: i
              };
            });
          }
        }
      }
    },
  };

  return (
    <div className="bg-white p-6 rounded-lg border" style={{ height }}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">Order Categories</h3>
        <p className="text-sm text-gray-600">Distribution of orders by food category</p>
      </div>
      <div style={{ height: height - 80 }}>
        <Doughnut data={chartData} options={options} />
      </div>
    </div>
  );
};

interface RevenueChartProps {
  data: RevenueData[];
  height?: number;
}

export const RevenueChart: React.FC<RevenueChartProps> = ({ data, height = 300 }) => {
  const chartData = {
    labels: data.map(item => item.period),
    datasets: [
      {
        label: 'Revenues',
        data: data.map(item => item.revenue),
        borderColor: chartColors.primary,
        backgroundColor: 'rgba(22, 163, 74, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: chartColors.primary,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const options = {
    ...lineChartConfig,
    plugins: {
      ...lineChartConfig.plugins,
      tooltip: {
        ...lineChartConfig.plugins.tooltip,
        callbacks: {
          label: function(context: any) {
            const value = context.parsed.y;
            const dataPoint = data[context.dataIndex];
            return [
              `Revenue: ₦${value.toLocaleString()}`,
              `Orders: ${dataPoint.orders.toLocaleString()}`,
              `Avg Order Value: ₦${dataPoint.averageOrderValue.toLocaleString()}`
            ];
          }
        }
      },
      legend: {
        display: false
      }
    },
    scales: {
      ...lineChartConfig.scales,
      y: {
        ...lineChartConfig.scales?.y,
        ticks: {
          callback: function(value: any) {
            return '₦' + (value / 1000) + 'K';
          }
        }
      }
    },
    elements: {
      line: {
        borderWidth: 3,
      },
    },
  };

  return (
    <div className="bg-white p-6 rounded-lg border" style={{ height }}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">Revenue Trend</h3>
        <p className="text-sm text-gray-600">Weekly revenue performance</p>
      </div>
      <div style={{ height: height - 80 }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

interface MiniChartProps {
  data: number[];
  color?: string;
  type?: 'line' | 'bar';
  height?: number;
}

export const MiniChart: React.FC<MiniChartProps> = ({ 
  data, 
  color = chartColors.primary, 
  type = 'line',
  height = 60 
}) => {
  const chartData = {
    labels: data.map((_, index) => index.toString()),
    datasets: [
      {
        data: data,
        borderColor: color,
        backgroundColor: type === 'line' ? 'transparent' : color + '20',
        fill: type === 'line',
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
        beginAtZero: true,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
  };

  return (
    <div style={{ height }}>
      {type === 'line' ? (
        <Line data={chartData} options={options} />
      ) : (
        <Bar data={chartData} options={options} />
      )}
    </div>
  );
};

interface MetricCardProps {
  title: string;
  value: string | number;
  color?: 'green' | 'blue' | 'purple' | 'orange' | 'gray';
  icon?: React.ReactNode;
  miniChartData?: number[];
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  color = 'green',
}) => {
  const getColorClasses = (color: string, isSelected: boolean = false) => {
    const baseClasses = isSelected ? 'border-2' : 'border';
    
    switch (color) {
      case 'green':
        return `${baseClasses} ${isSelected ? 'border-green-500 bg-green-50' : 'border-green-200'} text-green-700`;
      case 'blue':
        return `${baseClasses} ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-blue-200'} text-blue-700`;
      case 'purple':
        return `${baseClasses} ${isSelected ? 'border-purple-500 bg-purple-50' : 'border-purple-200'} text-purple-700`;
      case 'orange':
        return `${baseClasses} ${isSelected ? 'border-orange-500 bg-orange-50' : 'border-orange-200'} text-orange-700`;
      default:
        return `${baseClasses} ${isSelected ? 'border-gray-500 bg-gray-50' : 'border-gray-200'} text-gray-700`;
    }
  };

  return (
    <div className={`bg-white rounded-lg p-6 ${getColorClasses(color)} transition-all duration-200 hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">{title}</h3>
          </div>
          <div className="mb-2">
            <p className="text-3xl font-bold text-gray-900">{value}</p>
          </div>
        </div>
      </div>
    </div>
  );
};